import { FaBell, FaProjectDiagram, FaCompressArrowsAlt, FaUsers, FaTrashAlt } from 'react-icons/fa';
import SlaCountdownChip from './SlaCountdownChip';

// Constants
const STATUSES = ["Open", "On-hold", "Pending", "Solved", "Cancelled", "Closed"];
const PRIORITY_OPTIONS = ["Low", "Medium", "High", "Critical"];

/**
 * Returns true when the action item type qualifies for IT features (SLA chip, etc.).
 * Mirrors the same helper in ActionItemDetailPage.jsx and SlaInitializationService.cs.
 */
const isItType = (type) => {
    if (!type) return false;
    const lower = type.toLowerCase();
    if (lower.startsWith('it-') || lower.startsWith('it ')) return true;
    return lower.includes('it support') || lower.includes('it service') || lower.includes('it incident');
};

/**
 * ActionItemsListView Component
 * Renders a table view of action items with multi-select and inline editing capabilities.
 * Supports selecting multiple items for merging and updating status/priority/assignee inline.
 * IT tickets with an active SLA policy show a live SLA countdown chip in a dedicated column.
 * 
 * @param {Object} props - Component props
 * @param {Array} props.filteredItems - Array of filtered action items to display
 * @param {Set} props.selectedItems - Set of selected item IDs
 * @param {Function} props.onToggleSelection - Function to toggle item selection
 * @param {Function} props.onSelectAll - Function to select/deselect all items
 * @param {Function} props.onClearSelections - Function to clear all selections
 * @param {Function} props.onOpenMergeModal - Function to open merge modal
 * @param {Function} props.onAssigneeChange - Function to handle assignee change
 * @param {Function} props.onStatusChange - Function to handle status change
 * @param {Function} props.onPriorityChange - Function to handle priority change
 * @param {Function} props.navigate - Navigation function
 * @param {Array} props.propertiesMDA - Array of properties for lookup
 * @param {Array} props.allUsers - Array of all users for assignee dropdown
 * @param {Function} props.needsMyAttention - Function to check if item needs current user's attention
 * @param {Function} props.getItemIcon - Function to get icon for the item
 * @param {Function} props.getItemDisplayText - Function to get display text for the item
 * @param {Function} props.getUserDisplayName - Function to get display name for a user
 * @param {Function} props.getProjectName - Function to get project name from ID
 * @param {Function} props.isAssignedToMyDepartment - Function to check if item is assigned to user's department
 * @param {boolean} props.isAdmin - Whether the current user has admin privileges (controls delete button visibility)
 * @param {Function} props.onDeleteItem - Callback invoked with item ID when admin clicks delete
 * @param {Object} props.columnMeta - Pagination metadata per status column ({ [status]: { hasMore, totalCount, loading } })
 * @param {Function} props.onLoadMore - Callback to load more items for a given status column
 * @param {boolean} props.showClosedItems - Whether closed items are currently shown
 */
const ActionItemsListView = ({
    filteredItems,
    selectedItems,
    onToggleSelection,
    onSelectAll,
    onClearSelections,
    onOpenMergeModal,
    onAssigneeChange,
    onStatusChange,
    onPriorityChange,
    navigate,
    propertiesMDA,
    allUsers,
    needsMyAttention,
    getItemIcon,
    getItemDisplayText,
    getUserDisplayName,
    getProjectName,
    isAssignedToMyDepartment,
    isAdmin,
    onDeleteItem,
    columnMeta = {},
    onLoadMore,
    showClosedItems
}) => {
    // Determine whether any item in the current view has SLA data so we can
    // conditionally render the SLA column header (avoids an empty column when
    // no IT tickets are visible).
    const hasAnySla = filteredItems.some(item => isItType(item.actionItemsType) && item.sla);

    // Determine if any column still has more items to load
    const statusesToCheck = showClosedItems ? STATUSES : STATUSES.filter(s => s !== 'Closed');
    const columnsWithMore = statusesToCheck.filter(status => columnMeta[status]?.hasMore);
    const hasMoreItems = columnsWithMore.length > 0;
    const isLoadingMore = statusesToCheck.some(status => columnMeta[status]?.loading);

    // Calculate total loaded vs total available
    const totalLoaded = filteredItems.length;
    const totalAvailable = statusesToCheck.reduce((sum, status) => {
        return sum + (columnMeta[status]?.totalCount || 0);
    }, 0);

    // Load more for all columns that have remaining items
    const handleLoadMore = () => {
        if (onLoadMore) {
            columnsWithMore.forEach(status => onLoadMore(status));
        }
    };

    return (
        <div className="list-view-container">
            {/* Selection toolbar - shows when items are selected */}
            {selectedItems.size > 0 && (
                <div className="selection-toolbar">
                    <span className="selection-count">{selectedItems.size} item(s) selected</span>
                    <button
                        className="btn-merge"
                        onClick={onOpenMergeModal}
                        disabled={selectedItems.size < 2}
                        title="Merge selected items into one"
                    >
                        <FaCompressArrowsAlt /> Merge Selected
                    </button>
                    <button
                        className="btn-clear-selection"
                        onClick={onClearSelections}
                    >
                        Clear Selection
                    </button>
                </div>
            )}

            <table className="action-items-list-table">
                <thead>
                    <tr>
                        <th style={{ width: '40px' }}>
                            <input
                                type="checkbox"
                                checked={filteredItems.length > 0 && selectedItems.size === filteredItems.length}
                                onChange={onSelectAll}
                                title="Select all"
                            />
                        </th>
                        <th>ActionItem</th>
                        <th>Assignee</th>
                        <th>Status</th>
                        <th>Priority</th>
                        <th>Property</th>
                        <th>Tenant</th>
                        {/* SLA column — only rendered when at least one IT ticket is visible */}
                        {hasAnySla && <th style={{ width: '90px' }}>SLA</th>}
                        {isAdmin && <th style={{ width: '60px' }}>Actions</th>}
                    </tr>
                </thead>
                <tbody>
                    {filteredItems.map(item => {
                        const property = item.propertyID
                            ? propertiesMDA?.find(p => p.propertyID === item.propertyID)
                            : undefined;
                        const propertyName = property ? property.propertyName : '';
                        const itemNeedsAttention = needsMyAttention(item);
                        const isDepartmentAssigned = isAssignedToMyDepartment && isAssignedToMyDepartment(item);
                        const isSelected = selectedItems.has(item.id);
                        const showSla = isItType(item.actionItemsType) && item.sla;

                        return (
                            <tr
                                key={item.id}
                                onClick={() => navigate(`/action-items/${item.id}`)}
                                className={`clickable-row ${itemNeedsAttention ? 'needs-attention' : ''} ${isDepartmentAssigned ? 'department-assigned' : ''} ${isSelected ? 'selected-row' : ''}`}
                            >
                                <td onClick={(e) => e.stopPropagation()}>
                                    <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={() => onToggleSelection(item.id)}
                                    />
                                </td>
                                <td>
                                    {/* Template icon */}
                                    <i className={`fas ${getItemIcon(item)} action-item-icon`} title={item.templateKey || item.actionItemsType || 'Action Item'}></i>
                                    {/* Attention indicator */}
                                    {itemNeedsAttention && (
                                        <span className="attention-badge" title="This item needs your attention">
                                            <FaBell />
                                        </span>
                                    )}
                                    {/* Department assignment indicator */}
                                    {isDepartmentAssigned && (
                                        <span className="department-badge" title="Assigned to your department - not yet picked up">
                                            <FaUsers />
                                        </span>
                                    )}
                                    <span className="item-title">{getItemDisplayText(item)}</span>
                                    <span className="item-id">#{item.id}</span>
                                    {/* Project link if associated with a project */}
                                    {item.projectId && getProjectName(item.projectId) && (
                                        <span
                                            className="project-link-badge"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                navigate(`/projects/${item.projectId}`);
                                            }}
                                            title="View Project"
                                        >
                                            <FaProjectDiagram /> {getProjectName(item.projectId)}
                                        </span>
                                    )}
                                </td>
                                <td>
                                    <select
                                        value={item.assignedToUserID || ""}
                                        onChange={e => {
                                            e.stopPropagation();
                                            onAssigneeChange(item.id, Number(e.target.value));
                                        }}
                                        onClick={e => e.stopPropagation()}
                                    >
                                        <option value="">Unassigned</option>
                                        {allUsers.map(u => {
                                            const userId = u.userID || u.id;
                                            const displayName = getUserDisplayName(u) || `User ${userId}`;
                                            return (
                                                <option key={userId} value={userId}>
                                                    {displayName}
                                                </option>
                                            );
                                        })}
                                    </select>
                                </td>
                                <td>
                                    <select
                                        value={item.status}
                                        onChange={e => {
                                            e.stopPropagation();
                                            onStatusChange(item.id, e.target.value);
                                        }}
                                        onClick={e => e.stopPropagation()}
                                    >
                                        {STATUSES.map(status => (
                                            <option key={status} value={status}>{status}</option>
                                        ))}
                                    </select>
                                </td>
                                <td>
                                    <select
                                        value={item.priority}
                                        onChange={e => {
                                            e.stopPropagation();
                                            onPriorityChange(item.id, e.target.value);
                                        }}
                                        onClick={e => e.stopPropagation()}
                                    >
                                        {PRIORITY_OPTIONS.map(opt => (
                                            <option key={opt} value={opt}>{opt}</option>
                                        ))}
                                    </select>
                                </td>
                                <td className="table-bold">{propertyName}</td>
                                <td className="table-bold">{item.tenant}</td>
                                {/* SLA countdown chip cell — empty for non-IT tickets */}
                                {hasAnySla && (
                                    <td onClick={(e) => e.stopPropagation()}>
                                        {showSla && <SlaCountdownChip sla={item.sla} variant="row" />}
                                    </td>
                                )}
                                {isAdmin && (
                                    <td onClick={(e) => e.stopPropagation()}>
                                        <button
                                            className="btn-list-delete"
                                            onClick={() => onDeleteItem && onDeleteItem(item.id)}
                                            title="Permanently delete this action item"
                                        >
                                            <FaTrashAlt size={13} />
                                        </button>
                                    </td>
                                )}
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            {/* Load More button — shown when any status column has more items to fetch */}
            {hasMoreItems && (
                <div className="list-view-load-more">
                    <button
                        className="btn-load-more"
                        onClick={handleLoadMore}
                        disabled={isLoadingMore}
                    >
                        {isLoadingMore
                            ? 'Loading...'
                            : `Load More (showing ${totalLoaded} of ${totalAvailable})`}
                    </button>
                </div>
            )}
        </div>
    );
};

export default ActionItemsListView;